import { Directive, EventEmitter, Input, NgZone, OnDestroy, OnInit, Output } from '@angular/core';
import { DropDownListComponent } from '@progress/kendo-angular-dropdowns';
import { fromEvent, merge, Subject } from 'rxjs';
import { debounceTime, filter, takeUntil } from 'rxjs/operators';

export interface ChunkLoadingOptions {
  /** Distance from bottom in pixels to trigger load. Default: 50 */
  threshold?: number;
  /** Debounce time in ms for scroll events. Default: 100 */
  debounceMs?: number;
  /** Debounce time in ms for filter events. Default: 300 */
  filterDebounceMs?: number;
}

export interface ChunkLoadEvent {
  /** Current number of loaded items */
  skip: number;
  /** Triggered by scroll reaching threshold */
  isScrollTrigger: boolean;
  /** Search/filter clause */
  searchClause?: string;
}

@Directive({
  selector: 'kendo-dropdownlist[tiChunkLoading]',
  standalone: true,
})
export class KendoDropdownChunkLoadingDirective implements OnInit, OnDestroy {
  @Input('tiChunkLoading') options: ChunkLoadingOptions = {};

  /** Bind to the current data array length to track loaded items */
  @Input() set loadedCount(count: number) {
    const countChanged = count !== this._loadedCount;
    const previousCount = this._loadedCount;
    this._loadedCount = count;

    if (countChanged && previousCount > 0) {
      this.restoreScrollPosition(previousCount);
    }
  }

  /** Bind to loading state - removes overlay when loading completes */
  @Input() set chunkLoadingState(loading: boolean) {
    if (!loading) {
      this.removeOverlay();
      // Re-suppress after every load to block programmatic scroll events
      // (Kendo auto-scroll to selected item, restoreScrollPosition, etc.)
      // Cleared only by real user interaction (wheel/pointer) in setupUserScrollDetection
      this.suppressScrollLoad = true;
    }
  }

  /** Whether there are more items to load */
  @Input() hasMore = true;

  /** Emitted when more data should be loaded */
  @Output() loadMore = new EventEmitter<ChunkLoadEvent>();

  /** Emitted when dropdown is opened (for initial load) */
  @Output() chunkInit = new EventEmitter<void>();

  private readonly destroyed$ = new Subject<void>();
  private readonly popupClosed$ = new Subject<void>();
  private readonly filterChange$ = new Subject<string>();
  private _loadedCount = 0;
  private currentSearchClause = '';
  private listContentEl: HTMLElement | null = null;
  private overlayEl: HTMLElement | null = null;
  private isOpen = false;
  /** Suppresses scroll-triggered loads to prevent programmatic scrolls (Kendo auto-scroll, restoreScrollPosition) from firing loadMore */
  private suppressScrollLoad = false;

  /** Current loading state */
  public get isLoading(): boolean {
    return this.component.loading;
  }

  private get threshold(): number {
    return this.options?.threshold ?? 50;
  }

  private get debounceMs(): number {
    return this.options?.debounceMs ?? 100;
  }

  private get filterDebounceMs(): number {
    return this.options?.filterDebounceMs ?? 300;
  }

  constructor(
    private readonly component: DropDownListComponent,
    private readonly ngZone: NgZone,
  ) {}

  ngOnInit(): void {
    if (!this.options) {
      return;
    }

    // Handle filter changes with debounce
    this.filterChange$
      .pipe(takeUntil(this.destroyed$), debounceTime(this.filterDebounceMs))
      .subscribe((searchClause) => {
        this.onFilterChange(searchClause);
      });

    this.component.opened.pipe(takeUntil(this.destroyed$)).subscribe(() => {
      this.onDropdownOpened();
    });

    this.component.closed.pipe(takeUntil(this.destroyed$)).subscribe(() => {
      this.onDropdownClosed();
    });

    this.component.filterChange.pipe(takeUntil(this.destroyed$)).subscribe((value: string) => {
      this.filterChange$.next(value);
    });
  }

  ngOnDestroy(): void {
    this.destroyed$.next();
    this.destroyed$.complete();
    this.popupClosed$.next();
    this.popupClosed$.complete();
  }

  /** Reset the loaded count and search clause (call when data source changes) */
  public reset(): void {
    this._loadedCount = 0;
    this.currentSearchClause = '';
    this.suppressScrollLoad = false;
    this.removeOverlay();
  }

  private restoreScrollPosition(previousCount: number): void {
    setTimeout(() => {
      if (!this.listContentEl) return;

      const itemHeight = typeof this.component.virtual === 'object' ? this.component.virtual.itemHeight : 36;

      this.listContentEl.scrollTop = (previousCount - 2) * itemHeight;
      // Ensure Kendo's virtual scroll recalculates even if scrollTop didn't change
      this.listContentEl.dispatchEvent(new Event('scroll'));
    });
  }

  private onDropdownOpened(): void {
    this.isOpen = true;
    this.suppressScrollLoad = true;
    this.currentSearchClause = '';
    this._loadedCount = 0;

    this.ngZone.run(() => {
      this.chunkInit.emit();
    });

    // Wait for popup to render
    setTimeout(() => {
      this.setupScrollListener();
    }, 0);
  }

  private onFilterChange(searchClause: string): void {
    // Prevent emission when dropdown is closed (Kendo clears filter on close)
    if (!this.isOpen) return;

    this.currentSearchClause = searchClause;

    this.ngZone.run(() => {
      this.showOverlay();
      this.loadMore.emit({
        skip: 0,
        isScrollTrigger: false,
        searchClause,
      });
    });

    // Safety net: if response was synchronous (cache hit), loading signal
    // never toggled so chunkLoadingState setter won't fire to remove overlay.
    // setTimeout runs after Angular CD, so we can check the final loading state.
    setTimeout(() => {
      if (this.overlayEl && !this.component.loading) {
        this.removeOverlay();
      }
    });
  }

  private onDropdownClosed(): void {
    this.isOpen = false;
    this.suppressScrollLoad = false;
    this.popupClosed$.next();
    this.listContentEl = null;
    this.removeOverlay();
  }

  private setupScrollListener(): void {
    const popupEl = this.component.popupRef?.popupElement;
    if (!popupEl) return;

    this.listContentEl = popupEl.querySelector('.k-list-content');
    if (!this.listContentEl) return;

    fromEvent(this.listContentEl, 'scroll')
      .pipe(
        takeUntil(this.popupClosed$),
        takeUntil(this.destroyed$),
        debounceTime(this.debounceMs),
        filter(() => !this.component.loading),
      )
      .subscribe((event) => {
        this.onScroll(event);
      });

    this.setupUserScrollDetection();
  }

  /** Clears suppressScrollLoad on real user interaction (wheel, touch, scrollbar drag) */
  private setupUserScrollDetection(): void {
    if (!this.listContentEl) return;

    merge(fromEvent(this.listContentEl, 'wheel'), fromEvent(this.listContentEl, 'pointerdown'))
      .pipe(takeUntil(this.popupClosed$), takeUntil(this.destroyed$))
      .subscribe(() => {
        this.suppressScrollLoad = false;
      });
  }

  private onScroll(event: Event): void {
    if (this.suppressScrollLoad) return;

    const target = event.target as HTMLElement;
    const scrollTop = target.scrollTop;
    const scrollHeight = target.scrollHeight;
    const clientHeight = target.clientHeight;

    const distanceFromBottom = scrollHeight - scrollTop - clientHeight;

    if (distanceFromBottom <= this.threshold && this.hasMore) {
      this.ngZone.run(() => {
        this.showOverlay();
        this.loadMore.emit({
          skip: this._loadedCount,
          isScrollTrigger: true,
          searchClause: this.currentSearchClause,
        });
      });

      // Safety net for synchronous (cached) responses
      setTimeout(() => {
        if (this.overlayEl && !this.component.loading) {
          this.removeOverlay();
        }
      });
    }
  }

  private showOverlay(): void {
    if (this.overlayEl || !this.listContentEl) return;

    const parent = this.listContentEl.parentElement;
    if (!parent) return;

    // Ensure parent has relative positioning for overlay
    const parentPosition = getComputedStyle(parent).position;
    if (parentPosition === 'static') {
      parent.style.position = 'relative';
    }

    this.overlayEl = document.createElement('div');
    const spinner = document.createElement('div');

    spinner.classList.add('new-spinner');
    this.overlayEl.appendChild(spinner);
    this.overlayEl.style.cssText = `
      position: absolute;
      top: 0;
      left: 0;
      right: 0;
      bottom: 0;
      background: rgba(255, 255, 255, 0.3);
      z-index: 1;
      pointer-events: all;
    `;

    parent.appendChild(this.overlayEl);
  }

  private removeOverlay(): void {
    if (this.overlayEl) {
      this.overlayEl.remove();
      this.overlayEl = null;
    }
  }
}
